import { useWhoAmI } from '@api/get-whoami';
import { useUploadUserPrivatePicture } from '@api/upload-user-private-picture';
import { ProfilePictureUpload } from '@components/molecules/ProfilePictureUpload';
import {
	Button,
	TextInput,
	Title,
} from '@components/v2/ui';
import { LanguageSelect } from '@components/v2/ui/LanguageSelect/LanguageSelect';
import {
	REGEX_EMAIL,
	REGEX_PHONE_NUMBER,
} from '@config/constants/regex';
import { useUpdateProfile } from '@features/account-settings/api/update-profile';
import {
	Box,
	Flex,
	Group,
	Skeleton,
	Space,
} from '@mantine/core';
import {
	isNotEmpty,
	matches,
	useForm,
} from '@mantine/form';
import { useMediaQuery } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProfileChangeEmail from '../ProfileChangeEmail/ProfileChangeEmail';
import classes from './ProfileForm.module.css';

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */
const LANGUAGES_OPTIONS = [
	{
		label: 'Français',
		value: 'fr',
		flagUrl: 'https://flagcdn.com/w80/fr.png',
	},
	{
		label: 'English',
		value: 'en',
		flagUrl: 'https://flagcdn.com/w80/gb.png',
	},
];

export const ProfileForm = () => {
	/* ******************************** HOOKS ******************************* */
	const [pictureData, setPictureData] =
		useState<null | FormData>(null);

	const { t } = useTranslation();

	// Validators
	const formContact = useForm<any>({
		initialValues: {
			firstName: '',
			lastName: '',
			email: '',
			language: 'fr',
			phoneNumber: '',
			avatar: null,
		},
		validate: {
			firstName: isNotEmpty(
				t('global.firstNameRequired')
			),
			lastName: isNotEmpty(t('global.lastNameRequired')),
			email: matches(
				REGEX_EMAIL,
				t('form.error.invalidEmail')
			),
			phoneNumber: matches(
				REGEX_PHONE_NUMBER,
				t('form.error.invalidPhoneNumber')
			),
		},
	});

	const uploadPictureMutation =
		useUploadUserPrivatePicture({
			mutationConfig: {
				onSuccess(data) {
					formContact.setFieldValue('avatar', data.id);
					formContact.validate();
					if (formContact.isValid()) {
						handleUpdateProfile(formContact.getValues());
					}
				},
			},
		});

	const updateProfileMutation = useUpdateProfile({
		mutationConfig: {
			onSuccess() {
				formContact.resetDirty();
			},
		},
	});

	const {
		data: currentUser,
		isFetched: isFetchedCurrentUser,
		isPending: isPendingCurrentUser,
	} = useWhoAmI();

	useEffect(() => {
		if (isFetchedCurrentUser && currentUser) {
			formContact.setValues({
				firstName: currentUser.firstName,
				lastName: currentUser.lastName,
				email: currentUser.email,
				language: currentUser.locale
					? currentUser.locale.language
					: 'fr',
				phoneNumber:
					currentUser?.phoneNumber == null
						? ''
						: currentUser?.phoneNumber,
				avatar: currentUser.avatar || null,
			});
			formContact.resetDirty();
		}
	}, [isFetchedCurrentUser]);

	const handleUpdateProfile = (data: any) => {
		const {
			language,
			phoneNumber,
			firstName,
			lastName,
			avatar,
		} = data;
		updateProfileMutation.mutate({
			id: currentUser?.id,
			...(formContact.isDirty('language')
				? {
						locale: {
							language,
						},
					}
				: {}),
			...(formContact.isDirty('avatar')
				? {
						avatar,
					}
				: {}),
			...(formContact.isDirty('firstName')
				? {
						firstName,
					}
				: {}),
			...(formContact.isDirty('lastName')
				? {
						lastName,
					}
				: {}),
			...(formContact.isDirty('phoneNumber')
				? {
						phoneNumber,
					}
				: {}),
			...(formContact.isDirty('phoneNumber')
				? {
						phoneNumber:
							phoneNumber !== '' ? phoneNumber : null,
					}
				: {}),
		});
	};

	const handleSaveChanges = () => {
		if (formContact.getValues().avatar !== null) {
			uploadPictureMutation.mutate(pictureData);
		} else {
			formContact.validate();
			if (formContact.isValid()) {
				handleUpdateProfile(formContact.getValues());
			}
		}
	};

	const handleOnUploadProfile = (
		formData: FormData | string | null
	) => {
		if (typeof formData !== 'string') {
			formContact.setFieldValue('avatar', formData);
			setPictureData(formData);
		} else {
			formContact.setFieldValue('avatar', null);
		}
	};

	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	if (isPendingCurrentUser) {
		return (
			<Box
				style={{
					display: 'flex',
					flex: 1.5,
					flexDirection: 'column',
					gap: '20px',
				}}
			>
				<Group justify="space-between">
					<Skeleton
						height={100}
						width={100}
						radius="md"
						circle
					/>
					<Skeleton height={40} width={260} radius="md" />
				</Group>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Space h={30} />
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
			</Box>
		);
	}

	return (
		<Box
			style={{
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				gap: '24px',
			}}
		>
			<form className={classes.form}>
				<Title order={6} c={'#1D3965'}>
					{t('global.personalInformation')}
				</Title>
				<ProfilePictureUpload
					onUpload={handleOnUploadProfile}
					src={currentUser?.avatar || ''}
				/>
				<Flex wrap={'wrap'} gap={'24px'} flex={1}>
					<Flex miw={'250px'} flex={1}>
						<TextInput
							maxLength={50}
							key={formContact.key('firstName')}
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.firstName')}
							placeholder={t('global.firstName')}
							{...formContact.getInputProps('firstName')}
						/>
					</Flex>
					<Flex miw={'250px'} flex={1}>
						<TextInput
							maxLength={50}
							key={formContact.key('lastName')}
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.lastName')}
							placeholder={t('global.lastName')}
							{...formContact.getInputProps('lastName')}
						/>
					</Flex>

					<Flex miw={'250px'} flex={1}>
						<LanguageSelect
							label={t('global.language')}
							value={
								formContact.getInputProps('language')
									.value
							}
							onChange={(val) =>
								formContact.setFieldValue('language', val)
							}
							options={LANGUAGES_OPTIONS}
							fullWidth
						/>
					</Flex>
				</Flex>

				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInput
							autoComplete={'off'}
							key={formContact.key('phoneNumber')}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.phoneNumber')}
							placeholder={t('global.phoneNumber')}
							{...formContact.getInputProps(
								'phoneNumber'
							)}
						/>
					</Box>
				</Box>
				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButton}>
						<Button
							variant="primary"
							disabled={!formContact.isDirty()}
							loading={updateProfileMutation.isPending}
							fullWidth
							size={'md'}
							onClick={handleSaveChanges}
						>
							{t('global.saveChanges')}
						</Button>
					</Box>
				</Box>
			</form>
			<ProfileChangeEmail />
		</Box>
	);
};
